import axios from "axios";
import {useEffect, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {useRecoilState, useRecoilValue, useSetRecoilState} from "recoil";
import clientLogger from '../../../../utils/clientLogger';
import {checkOrderStatus, subscribeToOrderStatus, initializeSocket, startAggressivePolling, verifyPaymentStatus} from "../../../api/orderStatusSocket";
import {OrderStatus} from "../../../constants/enums/orderStatus";
import "./bitpay.css";
import {secondaryModals, secondOverlayState, selectedShippingAddress, giftCheckoutState, paymentStatusSnackbarState} from "../../../Recoil/Atoms";
import bitclose from "./bitclose.svg";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const BitPayModal = ({data}) => {
  const [, setOverlay] = useRecoilState(secondOverlayState);
  const [timer, setTimer] = useState(600);
  const [isSecondsPassed, setIsSecondsPassed] = useState(false);
  const [isRefreshClicked, setIsRefreshClicked] = useState(false);
  const selectedAddress = useRecoilValue(selectedShippingAddress);
  const [orderStatus, setOrderStatus] = useState(OrderStatus.NOT_PAID);
  // PAID / ERROR handled once: the initial check, the socket/poll listener and
  // the success-page route can all report the same outcome.
  const doneRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const setModals = useSetRecoilState(secondaryModals);
  const setSnack = useSetRecoilState(paymentStatusSnackbarState);

  // GIFT-CARD orders (GC- prefixed id): success routes to /gift-success and the
  // photo-order status endpoints don't apply (webhook + gift-page poll handle it).
  const giftCheckout = useRecoilValue(giftCheckoutState);
  const isGiftOrder = () => {
    const oid = localStorage.getItem("orderId");
    return !!giftCheckout || (oid && oid.startsWith("GC-"));
  };
  const goSuccess = (oid) => {
    if (isGiftOrder()) {
      localStorage.removeItem("orderId");
      navigate(`/gift-success/${oid}`);
    } else {
      navigate(`/payment-success/${oid}`);
      setTimeout(() => localStorage.clear(), 100);
    }
  };

  // A failure is told by the global PaymentStatusSnackbar; "לנסות שוב"
  // reopens this modal's payment-method choice.
  const showFailure = () => setSnack({id: Date.now(), retry: "selectCard"});

  // Payment detected by THIS modal: close now and go to the success page -
  // its SuccessReveal is the confirmation (no snackbar on success).
  const succeed = (oid) => {
    if (doneRef.current) return;
    doneRef.current = true;
    goSuccess(oid);
    handleClose();
  };

  // Bit reported ERROR: close and show the failure snackbar (was errorCart).
  const fail = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    setOrderStatus(OrderStatus.ERROR);
    handleClose();
    localStorage.removeItem("orderId");
    showFailure();
  };

  // Redirect can also come from OUTSIDE the modal (the gift page's status poll
  // navigates on webhook activation): close at once. Without this the modal
  // used to stay open after the redirect.
  useEffect(() => {
    const p = location.pathname;
    if (!doneRef.current && (p.startsWith("/gift-success") || p.startsWith("/payment-success"))) {
      doneRef.current = true;
      handleClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    // Initialize socket connection
    initializeSocket();
    
    // Start aggressive polling immediately for Bit payments
    startAggressivePolling();

    // Do an initial check with enhanced verification. Gift orders skip it —
    // /user/checkorder doesn't apply to GC- ids; the socket + gift-page poll
    // handle the redirect.
    if (localStorage.getItem("orderId") && !isGiftOrder()) {
      verifyPaymentStatus().then((resp) => {
        if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
          clientLogger.debug('PAYMENT DETECTED - NAVIGATING TO SUCCESS');
          setOrderStatus(OrderStatus.PAID);
          window.successfulPaymentReceived = true;
          sessionStorage.setItem('successfulPaymentReceived', 'true');
          const oid = localStorage.getItem("orderId");

          succeed(oid);
        } else if (resp && resp.data && resp.data.orderStatus === OrderStatus.ERROR) {
          fail();
        } else {
          clientLogger.debug('Bit Payment: Waiting for payment completion...', resp?.data?.orderStatus);
        }
      }).catch((error) => {
        console.error('Bit Payment: Initial verification failed:', error);
      });
    }

    // Subscribe to status updates with enhanced logging
    const unsubscribe = subscribeToOrderStatus((resp) => {
      const orderId = localStorage.getItem("orderId");
      clientLogger.debug(`Bit Payment Desktop Status Update for Order ${orderId}`, {
        status: resp?.data?.orderStatus,
        timestamp: new Date().toISOString()
      });
      
      if (resp && resp.data && resp.data.orderStatus === OrderStatus.PAID) {
        clientLogger.debug(`Bit Payment Desktop SUCCESS for Order ${orderId}`);
        setOrderStatus(OrderStatus.PAID);
        window.successfulPaymentReceived = true;
        sessionStorage.setItem('successfulPaymentReceived', 'true');
        const oid = localStorage.getItem("orderId");

        succeed(oid);
      } else if (resp && resp.data && resp.data.orderStatus === OrderStatus.ERROR) {
        clientLogger.debug(`Bit Payment Desktop ERROR for Order ${orderId}`);
        fail();
      } else {
        clientLogger.debug(`Bit Payment Desktop Status for Order ${orderId}: ${resp?.data?.orderStatus}`);
      }
    });

    // Clean up subscription
    return () => {
      unsubscribe();
    };
  }, []);

  const handleBitPayment = async () => {
    if (isRefreshClicked) {
      return;
    }

    const checkResp = await checkOrderStatus();
    if (checkResp.data && checkResp.data.orderStatus === OrderStatus.PAID) {
      setOrderStatus(OrderStatus.PAID);
      return;
    }

    let gamaAuthResponse;
    const gamaAccessToken = localStorage.getItem("gamaAccessToken");
    const expiresAt = localStorage.getItem("gamaAccessTokenExpiresAt");

    try {
      if (!gamaAccessToken || expiresAt <= Date.now()) {
        gamaAuthResponse = await axios.post(`${BASE_URL}/gama-login`);

        if (gamaAuthResponse.data.status !== 200) {
          throw new Error("Gama auth failed.");
        }

        localStorage.setItem("gamaAccessToken", gamaAuthResponse.data.access_token);
        localStorage.setItem("gamaAccessTokenExpiresAt", (Date.now() + gamaAuthResponse.data.expires_in));
      }

      const gamaSessionResponse = await axios.post(`${BASE_URL}/gama-session`, {
        gamaAccessToken: localStorage.getItem("gamaAccessToken"),
      });

      if (gamaSessionResponse.data.status !== 200) {
        throw new Error("Gama session creating failed.");
      }

      const id = localStorage.getItem("orderId");

      const createTransactionResponse = await axios.post(`${BASE_URL}/gama-create-payment`, {
        orderId: id,
        amount: data,
        payerName: (isGiftOrder() && giftCheckout ? giftCheckout.buyerName : selectedAddress.fullName) || "Blends",
        payerPhoneNumber: isGiftOrder() && giftCheckout ? giftCheckout.buyerPhone : selectedAddress.phoneNumber,
        transactionId: gamaSessionResponse.data.transactionProcessingId,
        gamaAccessToken: localStorage.getItem("gamaAccessToken"),
        gamaSessionToken: gamaSessionResponse.data.token,
        transactionEndpoint: gamaSessionResponse.data.transactionProcessingEndpoint,
      });

      if (createTransactionResponse.data.status !== 200) {
        throw new Error("Gama transaction creating failed.");
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleClose = () => {
    setModals(state => ({
      ...state,
      bitPay: {visible: false},
      selectCard: {visible: false},
    }));
    setOverlay(false);
  };

  const handleRefreshClick = async () => {
    if (isSecondsPassed) {
      setTimer(600);
      await handleBitPayment();
      setIsRefreshClicked(true);
    }
  };

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    const formattedMinutes = String(minutes).padStart(2, "0");
    const formattedSeconds = String(remainingSeconds).padStart(2, "0");
    return `${formattedMinutes}:${formattedSeconds}`;
  };

  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimer((prevTimer) => {
        if (prevTimer > 0) {
          return prevTimer - 1;
        } else {
          clearInterval(intervalId);
          // Final check before closing - this is the key fix
          clientLogger.debug('TIMER EXPIRED - PERFORMING FINAL STATUS CHECK');
          performFinalStatusCheck();
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Function to perform final status check before modal closes
  const performFinalStatusCheck = async () => {
    try {
      clientLogger.debug('FINAL STATUS CHECK: Checking order status before closing modal');
      const orderId = localStorage.getItem("orderId");

      if (!orderId || isGiftOrder()) {
        // Gift orders: /user/checkorder doesn't apply — the gift-page poll
        // redirects on activation; just close.
        handleClose();
        return;
      }

      const response = await checkOrderStatus();
      clientLogger.debug('FINAL STATUS CHECK RESULT:', response?.data);

      if (response && response.data && response.data.orderStatus === OrderStatus.PAID) {
        clientLogger.debug('FINAL STATUS CHECK: PAYMENT COMPLETED - NAVIGATING TO SUCCESS');
        setOrderStatus(OrderStatus.PAID);

        succeed(orderId);
      } else {
        clientLogger.debug('FINAL STATUS CHECK: PAYMENT NOT COMPLETED - CLOSING MODAL');
        handleClose();
      }
    } catch (error) {
      console.error('FINAL STATUS CHECK ERROR:', error);
      // If there's an error checking status, still close the modal
      handleClose();
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsSecondsPassed(true);
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="bitbody-desktop">
      <img src={bitclose} onClick={handleClose} alt="" className="bitclose" />
      <div className="bitcontent">
        <div className="bitinfo">
          <div className="bitamount">
            ₪{data?.toLocaleString ? data.toLocaleString('he-IL', {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }) : data}
            <p>
              בקשה לתשלום מבלנדס:
              <br />
              {isGiftOrder() ? "גיפט קארד בלנדס" : "תמונות ממוסגרות"}
            </p>
          </div>
        </div>
        <img className="bitface" src="/assets/images/bitfaceblends.svg" alt="" />
      </div>
      <p className="paragraph">
        בחרתם לשלם באמצעות
        <img src="/assets/images/bitnobgblends.svg" alt="bit blends" />
        <br />
        ברגעים אלו נשלח אליכם מסרון לנייד לביצוע התשלום
      </p>
      <div className="buttons">
        <div className="timer-button">{formatTime(timer)}</div>
        <button className="button2" onClick={() => handleRefreshClick()}>שלח שוב</button>
      </div>
    </div>
  );
};

export default BitPayModal;
